/**
 * Cost-center grouping for the Pipeline Models board.
 *
 * The seven cost centers are the engine's own CHECK values (the same set
 * AICostsPage meters against). Here they are used twice:
 *   1. ORDER — the display order of the groups on the page. Cost centers that
 *      are not in the list (a newly seeded one) are appended alphabetically, so
 *      a new center shows up without a code change.
 *   2. TITLE — a friendly, human title for the sticky group header. The raw key
 *      still shows on each row's chip cluster, so nothing is hidden.
 *
 * Group collapse state persists to localStorage, DEFAULT EXPANDED — the same
 * pattern the Sidebar uses for its navigation groups.
 */

import { PipelineModel } from '../../../services/quillAdminService';

/** Display order for the seven engine cost centers. */
export const COST_CENTER_ORDER: string[] = [
  'conversation',
  'memory-extraction',
  'photo-pipeline',
  'social-ingestion',
  'speech',
  'prosody',
  'admin-tools',
];

const COST_CENTER_TITLE: Record<string, string> = {
  conversation: 'Conversation',
  'memory-extraction': 'Memory Extraction',
  'photo-pipeline': 'Photo Pipeline',
  'social-ingestion': 'Social Ingestion',
  speech: 'Speech',
  prosody: 'Prosody',
  'admin-tools': 'Admin Tools',
};

/** Friendly group title for a cost center; title-cases anything unrecognised. */
export function costCenterTitle(costCenter: string): string {
  const known = COST_CENTER_TITLE[costCenter];
  if (known) return known;
  return costCenter
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export interface PipelineGroupModel {
  /** Raw cost_center key — the group identity used for collapse persistence. */
  key: string;
  /** Friendly header title. */
  title: string;
  pipelines: PipelineModel[];
}

/**
 * Partitions pipelines by cost_center, ordered by COST_CENTER_ORDER first and
 * then alphabetically for anything unlisted. Empty groups are never emitted.
 */
export function groupPipelines(pipelines: PipelineModel[]): PipelineGroupModel[] {
  const byCostCenter = new Map<string, PipelineModel[]>();
  pipelines.forEach((p) => {
    const key = p.cost_center || 'other';
    const bucket = byCostCenter.get(key);
    if (bucket) bucket.push(p);
    else byCostCenter.set(key, [p]);
  });

  const ordered: string[] = [];
  COST_CENTER_ORDER.forEach((cc) => {
    if (byCostCenter.has(cc)) ordered.push(cc);
  });
  Array.from(byCostCenter.keys())
    .sort()
    .forEach((cc) => {
      if (!ordered.includes(cc)) ordered.push(cc);
    });

  return ordered.map((cc) => ({
    key: cc,
    title: costCenterTitle(cc),
    pipelines: byCostCenter.get(cc) as PipelineModel[],
  }));
}

// ── Collapse persistence ──────────────────────────────────────────────
export const PIPELINE_GROUPS_STORAGE_KEY = 'lsadmin.pipelineGroups.expanded.v1';

/**
 * Reads the persisted group-collapse map. A group absent from the map is
 * EXPANDED — the stored object only ever records deviations from the default,
 * so a newly seeded cost center is open the first time it appears.
 */
export function readGroupExpanded(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(PIPELINE_GROUPS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, boolean>;
      }
    }
  } catch {
    // Malformed / unavailable storage — everything defaults to expanded.
  }
  return {};
}

export function writeGroupExpanded(state: Record<string, boolean>): void {
  try {
    window.localStorage.setItem(PIPELINE_GROUPS_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Private mode / quota — collapse state is a convenience, never a blocker.
  }
}
